import { useEffect, useState } from "react";
import { Button, Form, Offcanvas } from "react-bootstrap";
import { FaBuilding, FaEnvelope, FaLocationDot, FaPenToSquare, FaPhone, FaUserTie } from "react-icons/fa6";
import { toast } from "react-toastify";
import type { ExpertProfile } from "../../../types/Profiles/expertProfile.types";
import { updateExpertProfile } from "../../../services/expertProfile/expertProfile";

interface Props {
  profile: ExpertProfile;
  userId?: number | string | null;
  onProfileUpdated?: () => Promise<void> | void;
}

const emptyForm = {
  companyName: "",
  expertType: "",
  siteName: "",
  wilaya: "",
  address: "",
  location: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  status: "Expert",
  aidaBadge: "aida_verified",
  domainsText: "",
  certificationsText: "",
  supportNeedsText: "",
};

const toLines = (value: string) => value.split(/\n+/).map((item) => item.trim()).filter(Boolean);

const InfoCard = ({ icon, label, value, color = "#D39E00", bg = "#FFF4D6" }: { icon: JSX.Element; label: string; value?: string; color?: string; bg?: string }) => (
  <div className="col-xl-3 col-md-6">
    <div className="border rounded-4 p-4 h-100 bg-white" style={{ minHeight: 150 }}>
      <div className="d-flex align-items-start gap-3">
        <div className="d-flex align-items-center justify-content-center rounded-4" style={{ width: 52, height: 52, backgroundColor: bg, color, fontSize: 20, flexShrink: 0 }}>{icon}</div>
        <div className="w-100">
          <small className="text-muted d-block mb-1">{label}</small>
          <div className="fw-semibold" style={{ wordBreak: "break-word", lineHeight: 1.4 }}>{value || "-"}</div>
        </div>
      </div>
    </div>
  </div>
);

export default function ExpertCompanyTab({ profile, userId, onProfileUpdated }: Props) {
  const [showDrawer, setShowDrawer] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const syncForm = () => {
    setFormData({
      companyName: profile.identity.companyName || "",
      expertType: profile.identity.expertType || "expert",
      siteName: profile.identity.siteName || "",
      wilaya: profile.identity.wilaya || "",
      address: profile.identity.address || "",
      location: profile.identity.location || "",
      contactName: profile.identity.contactName || "",
      contactEmail: profile.identity.contactEmail || "",
      contactPhone: profile.identity.contactPhone || "",
      status: profile.identity.status || "Expert",
      aidaBadge: profile.identity.aidaBadge || "aida_verified",
      domainsText: profile.domains.join("\n"),
      certificationsText: profile.certifications.join("\n"),
      supportNeedsText: profile.supportNeeds.join("\n"),
    });
  };

  useEffect(() => {
    syncForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const handleOpenDrawer = () => {
    syncForm();
    setShowDrawer(true);
  };

  const handleSave = async () => {
    try {
      await updateExpertProfile({
        ...formData,
        domains: toLines(formData.domainsText),
        certifications: toLines(formData.certificationsText),
        supportNeeds: toLines(formData.supportNeedsText),
      }, userId);
      toast.success("Profil expert modifie avec succes");
      setShowDrawer(false);
      await onProfileUpdated?.();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.error || "Erreur pendant la modification du profil expert");
    }
  };

  const updateField = (name: string, value: string) => setFormData((prev) => ({ ...prev, [name]: value }));

  return (
    <>
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="fw-semibold mb-1">Informations de l'entreprise</h4>
              <p className="text-muted mb-0">Identite de l'expert, localisation et informations de contact.</p>
            </div>
            <Button variant="warning" className="rounded-3 px-4 fw-semibold d-inline-flex align-items-center gap-2" onClick={handleOpenDrawer}>
              <FaPenToSquare size={15} /> Modifier les informations de l'entreprise
            </Button>
          </div>

          <div className="row g-4">
            <InfoCard icon={<FaBuilding />} label="Nom de l'entreprise" value={profile.identity.companyName} />
            <InfoCard icon={<FaUserTie />} label="Type expert" value={profile.identity.expertType} bg="#EAF2FF" color="#0D6EFD" />
            <InfoCard icon={<FaEnvelope />} label="Email de contact" value={profile.identity.contactEmail} bg="#F3EFFF" color="#7C3AED" />
            <InfoCard icon={<FaPhone />} label="Telephone de contact" value={profile.identity.contactPhone} bg="#FFF1F0" color="#DC3545" />
            <InfoCard icon={<FaLocationDot />} label="Wilaya" value={profile.identity.wilaya} bg="#EEF2F7" color="#495057" />
            <InfoCard icon={<FaBuilding />} label="Ville / site" value={profile.identity.siteName} bg="#F5F3FF" color="#6F42C1" />
            <InfoCard icon={<FaBuilding />} label="Adresse" value={profile.identity.address || profile.identity.location} />
            <InfoCard icon={<FaUserTie />} label="Responsable" value={profile.identity.contactName} bg="#EAF8EE" color="#198754" />
            <InfoCard icon={<FaBuilding />} label="Badge AIDA" value={profile.identity.aidaBadge} bg="#EAF2FF" color="#0D6EFD" />
          </div>
        </div>
      </div>

      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="end" backdrop style={{ width: window.innerWidth > 992 ? "50vw" : "100%" }}>
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-semibold">Modifier le profil expert</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <div className="row g-3">
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Nom de l'entreprise</Form.Label><Form.Control value={formData.companyName} onChange={(e) => updateField("companyName", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Type expert</Form.Label><Form.Control value={formData.expertType} onChange={(e) => updateField("expertType", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Responsable</Form.Label><Form.Control value={formData.contactName} onChange={(e) => updateField("contactName", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Email</Form.Label><Form.Control type="email" value={formData.contactEmail} onChange={(e) => updateField("contactEmail", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Telephone</Form.Label><Form.Control value={formData.contactPhone} onChange={(e) => updateField("contactPhone", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Wilaya</Form.Label><Form.Control value={formData.wilaya} onChange={(e) => updateField("wilaya", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Ville / site</Form.Label><Form.Control value={formData.siteName} onChange={(e) => updateField("siteName", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Adresse</Form.Label><Form.Control value={formData.address} onChange={(e) => updateField("address", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Localisation</Form.Label><Form.Control value={formData.location} onChange={(e) => updateField("location", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Statut</Form.Label><Form.Control value={formData.status} onChange={(e) => updateField("status", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-md-6"><Form.Label className="fw-semibold">Badge AIDA</Form.Label><Form.Control value={formData.aidaBadge} onChange={(e) => updateField("aidaBadge", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-12"><Form.Label className="fw-semibold">Domaines d'expertise (un par ligne)</Form.Label><Form.Control as="textarea" rows={4} value={formData.domainsText} onChange={(e) => updateField("domainsText", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-12"><Form.Label className="fw-semibold">Certifications (une par ligne)</Form.Label><Form.Control as="textarea" rows={4} value={formData.certificationsText} onChange={(e) => updateField("certificationsText", e.target.value)} className="rounded-3" /></Form.Group>
              <Form.Group className="col-12"><Form.Label className="fw-semibold">Besoins / accompagnement (un par ligne)</Form.Label><Form.Control as="textarea" rows={4} value={formData.supportNeedsText} onChange={(e) => updateField("supportNeedsText", e.target.value)} className="rounded-3" /></Form.Group>
              <div className="d-flex justify-content-between mt-4"><Button variant="light" onClick={() => setShowDrawer(false)} className="rounded-3 px-4">Annuler</Button><Button variant="warning" onClick={handleSave} className="rounded-3 px-4 fw-semibold">Enregistrer</Button></div>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
